import { ChangeDetectionStrategy, ChangeDetectorRef, Component, OnDestroy, OnInit } from '@angular/core';
import { combineLatest, Observable, of, Subscription } from 'rxjs';
import { switchMap } from 'rxjs/operators';

import { CompareService } from '../../providers/compare/compare.service';
import { DataService } from '../../providers/data/data.service';

import { GET_COMPARE_PRODUCT } from './compare-page.graphql';

interface CompareProduct {
    id: string;
    name: string;
    slug: string;
    description: string;
    featuredAsset?: { preview: string } | null;
    facetValues: Array<{ name: string; facet: { code: string; name: string } }>;
    variants: Array<{ priceWithTax: number }>;
    customFields?: { specifications?: string | null } | null;
}

interface SpecPair {
    name: string;
    values: Array<string | null>;
}

@Component({
    selector: 'vsf-compare-page',
    templateUrl: './compare-page.component.html',
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ComparePageComponent implements OnInit, OnDestroy {
    readonly MAX = CompareService.MAX;
    products: (CompareProduct | null)[] = [];
    specRows: SpecPair[] = [];
    loading = false;
    private sub: Subscription;

    constructor(
        private compareService: CompareService,
        private dataService: DataService,
        private cdr: ChangeDetectorRef,
    ) {}

    ngOnInit() {
        this.sub = this.compareService.compareIds$
            .pipe(
                switchMap(ids => {
                    if (!ids.length) {
                        return of<(CompareProduct | null)[]>([]);
                    }
                    this.loading = true;
                    this.cdr.markForCheck();
                    const queries: Observable<CompareProduct | null>[] = ids.map(id =>
                        this.dataService
                            .query<{ product: CompareProduct | null }>(GET_COMPARE_PRODUCT, { id })
                            .pipe(switchMap(res => of(res?.product ?? null))),
                    );
                    return combineLatest(queries);
                }),
            )
            .subscribe(products => {
                this.products = products.slice(0, CompareService.MAX);
                this.specRows = this.buildSpecRows(this.products);
                this.loading = false;
                this.cdr.markForCheck();
            });
    }

    ngOnDestroy() {
        this.sub?.unsubscribe();
    }

    remove(id: string) {
        this.compareService.remove(id);
    }

    brand(product: CompareProduct | null): string {
        if (!product) return '';
        const brandFacet = product.facetValues.find(
            fv => fv.facet.code === 'brand' || fv.facet.code === 'brands',
        );
        return brandFacet?.name || '—';
    }

    columnCount(): number {
        return Math.max(2, this.products.length);
    }

    get placeholders(): number[] {
        return Array.from({ length: Math.max(0, CompareService.MAX - this.products.length) });
    }

    private buildSpecRows(products: (CompareProduct | null)[]): SpecPair[] {
        const flatSpecs: Array<Array<{ name: string; value: string }>> = products.map(p =>
            this.flattenSpecs(p?.customFields?.specifications),
        );
        const names: string[] = [];
        for (const list of flatSpecs) {
            for (const spec of list) {
                if (!names.includes(spec.name)) {
                    names.push(spec.name);
                }
            }
        }
        return names.map(name => ({
            name,
            values: flatSpecs.map(list => list.find(s => s.name === name)?.value ?? null),
        }));
    }

    private flattenSpecs(raw: any): Array<{ name: string; value: string }> {
        try {
            const parsed = typeof raw === 'string' ? JSON.parse(raw) : raw;
            if (!parsed) return [];
            if (Array.isArray(parsed)) {
                if (parsed.length && parsed[0]?.category !== undefined) {
                    return parsed.flatMap((cat: any) =>
                        Array.isArray(cat.specs)
                            ? cat.specs.map((s: any) => ({ name: String(s.name ?? ''), value: String(s.value ?? '') }))
                            : [],
                    );
                }
                return parsed.map((s: any) => ({ name: String(s.name ?? ''), value: String(s.value ?? '') }));
            }
        } catch {}
        return [];
    }
}

